import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState
} from "react";
import {
  Button,
  Grid,
  Typography,
  Stack,
  Skeleton
} from "@mui/material";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import Modal from "../../../Components/Modal";
import useGlobalContext from "../../../Hooks/useGlobalContext";
import { useSnackbar } from "notistack";
import Cookies from "js-cookie";

interface IPreserveEstruturaMercadologicaProps {
  reloadResourceTable: () => void;
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  spedFiscalIdSelecionado: number;
}

const PreserveEstruturaMercadologica: React.FC<IPreserveEstruturaMercadologicaProps> = ({
  reloadResourceTable,
  openModal,
  setOpenModal,
  spedFiscalIdSelecionado
}) => {
  const { setLoading, loading } = useGlobalContext();
  const { enqueueSnackbar } = useSnackbar();

  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  const isUploadEnabled = !!file && !loading;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null;
    setFile(selected);
  };

  const handleCloseModal = () => {
    setFile(null);
    setOpenModal(false);
  };

  const resetForm = () => {
    setFile(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  useEffect(() => {
    if (openModal) resetForm();
  }, [openModal]);

  const handleUpload = async () => {
    if (!file) return;

    setLoading?.(true);
    const authToken = Cookies.get("authTokenMHP");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("sped_fiscal_id", String(spedFiscalIdSelecionado));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}estruturaMercadologica`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const result = await response.json();

      if (result?.identifier) {
        enqueueSnackbar(result.message || "Arquivo importado com sucesso!", { variant: "success" });
        handleCloseModal();
        reloadResourceTable();
      } else {
        enqueueSnackbar(result?.message || "Erro ao importar o arquivo.", { variant: "error" });
      }
    } catch (error) {
      console.error("Erro ao importar estrutura:", error);
      enqueueSnackbar("Erro inesperado ao importar o arquivo.", { variant: "error" });
    } finally {
      setLoading?.(false);
    }
  };

  return (
    <Modal
      title="Importar novo Arquivo de Estrutura Mercadológica"
      open={openModal && !loading}
      closeModal={handleCloseModal}
      maxWidth="sm"
    >
      <Grid container spacing={0.5}>
        <Grid item xs={12}>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            aria-label="Selecionar arquivo CSV da estrutura mercadológica"
          >
            Selecione o arquivo de Estrutura Mercadológica (.csv)
            <input
              type="file"
              accept=".csv"
              hidden
              ref={inputRef}
              onChange={handleFileChange}
            />
          </Button>

          {file && (
            <Typography mt={1} variant="body2" color="textSecondary">
              Arquivo selecionado: <strong>{file.name}</strong>
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!isUploadEnabled}
              sx={{ backgroundColor: "#1c2e4a", color: "#FFF" }}
              startIcon={
                loading ? (
                  <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: "#FFF" }} />
                ) : (
                  <SaveAsIcon />
                )
              }
              aria-label="Importar estrutura mercadológica"
            >
              Importar
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default PreserveEstruturaMercadologica;
